import { Box, Stack } from '@mui/material';
import { colors, Typography, Button } from '@sweep-io/sweep-design';
import React, { useState, useMemo, useCallback } from 'react';
import EditTextarea from '../../../../common/editable-card/EditTextarea';
import ProcessMiningColored from '../process-mining/svg/process-mining-colored.svg?react';
import AgentPrompts from '../../../../aiAgentCenter/AgentPrompts';

const quickPrompts = [
  'Visualize all Opportunity configurations - including automations and Apex.',
  'Map all dependencies for the Opportunity.StageName field.',
];

interface ProcessMiningPromptProps {
  onSendMessage: (message: string) => void;
  error?: string;
  loading?: boolean;
  message: string | null;
}

export const ProcessMiningPrompt = ({
  onSendMessage,
  error,
  loading,
  message,
}: ProcessMiningPromptProps) => {
  const [inputValue, setInputValue] = useState('');

  const handleGenerate = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleQuickPrompt = useCallback(
    async (prompt: string) => {
      onSendMessage(prompt);
    },
    [onSendMessage],
  );

  const transformedPrompts = useMemo(
    () =>
      quickPrompts.map((prompt) => ({
        id: prompt,
        name: prompt,
        promptText: prompt,
      })),
    [],
  );

  return (
    <Stack gap={3}>
      <Box textAlign="center">
        <Typography variant="h1" color={colors.grey[800]}>
          Metadata Mapping
        </Typography>
      </Box>

      <Stack
        sx={{
          flex: 1,
          minHeight: '174px',
          padding: 2,
          borderRadius: '8px',
          border: '2px solid transparent',
          background: `linear-gradient(${colors.grey[100]}, ${colors.grey[100]}) padding-box, linear-gradient(to right, ${colors.blue[500]}, #EC7CAB) border-box`,
        }}
      >
        <Stack height="100%" flex={1}>
          <Stack flex={1}>
            <Stack flex={1} direction="row" gap={1} alignItems="flex-start">
              <Box width="22px">
                <ProcessMiningColored />
              </Box>
              <Stack flex={1}>
                <EditTextarea
                  setText={(newText: string) => setInputValue(newText)}
                  text={inputValue}
                  placeholder="What would you like to map?"
                  onSubmit={handleGenerate}
                  submitOnEnter={true}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              variant="primary"
              size="small"
              onClick={handleGenerate}
              disabled={!inputValue.trim() || loading}
              loading={loading}
            >
              Generate
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {error && (
        <Box
          sx={{
            backgroundColor: colors.grey[200],
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body">{error}</Typography>
        </Box>
      )}

      {message && (
        <Box
          sx={{
            backgroundColor: colors.grey[200],
            padding: '8px 16px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="caption" color={colors.grey[900]}>
            {message}
          </Typography>
        </Box>
      )}

      <Box alignSelf="flex-start" width="100%">
        <Box sx={{ mb: 1 }}>
          <Typography variant="body-medium" color={colors.grey[900]}>
            Try these
          </Typography>
        </Box>
        <AgentPrompts
          prompts={transformedPrompts}
          onClickPrompt={handleQuickPrompt}
          isDisabled={loading || false}
        />
      </Box>
    </Stack>
  );
};
